import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, getAuthHeader } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { CheckCircle, X, Building2, FileText, AlertCircle, Download, Eye } from 'lucide-react';
import { downloadFile } from '../utils/fileUtils';

const AccountsDashboard = ({ section }) => {
  const navigate = useNavigate();
  const [selectedSection, setSelectedSection] = useState(section || null); // null shows dashboard
  
  // Vendor states
  const [vendors, setVendors] = useState([]);
  const [vendorTab, setVendorTab] = useState('pending');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
  const [vendorActionType, setVendorActionType] = useState('');
  const [vendorRejectionReason, setVendorRejectionReason] = useState('');
  
  // Purchase Request states
  const [purchaseRequests, setPurchaseRequests] = useState([]);
  const [prTab, setPrTab] = useState('active');
  const [selectedPR, setSelectedPR] = useState(null);
  const [prDialogOpen, setPrDialogOpen] = useState(false);
  const [prActionType, setPrActionType] = useState('');
  const [additionalInfoRequest, setAdditionalInfoRequest] = useState('');
  const [poContent, setPoContent] = useState(null);
  const [poDialogOpen, setPoDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  
  const [loading, setLoading] = useState(false);
  
  // Pagination & Filter states for PRs
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [costCenterFilter, setCostCenterFilter] = useState('all');

  useEffect(() => {
    fetchVendors();
    fetchPurchaseRequests();
  }, []);

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/vendors`, { headers: getAuthHeader() });
      setVendors(response.data);
    } catch (error) {
      toast.error('Failed to fetch vendors');
    }
  };

  const fetchPurchaseRequests = async () => {
    try {
      const response = await axios.get(`${API}/purchase-requests`, { headers: getAuthHeader() });
      setPurchaseRequests(response.data);
    } catch (error) {
      toast.error('Failed to fetch purchase requests');
    }
  };

  const handleViewPo = async (prId) => {
    try {
      const response = await axios.get(`${API}/purchase-requests/${prId}/po-content`, {
        headers: getAuthHeader()
      });
      setPoContent(response.data);
      setPoDialogOpen(true);
    } catch (error) {
      toast.error('Failed to fetch PO content');
    }
  };

  // Vendor functions
  const getFilteredVendors = () => {
    if (vendorTab === 'pending') {
      return vendors.filter(v => v.approval_status === 'pending_approval');
    }
    return vendors.filter(v => v.approval_status === 'approved' || v.approval_status === 'rejected');
  };

  const handleVendorAction = async () => {
    if (vendorActionType === 'reject' && !vendorRejectionReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/vendors/${selectedVendor.id}/approve`,
        { action: vendorActionType, reason: vendorActionType === 'reject' ? vendorRejectionReason : null },
        { headers: getAuthHeader() }
      );
      toast.success(vendorActionType === 'approve' ? 'Vendor approved successfully!' : 'Vendor rejected');
      setVendorDialogOpen(false);
      setVendorRejectionReason('');
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  const openVendorActionDialog = (vendor, action) => {
    setSelectedVendor(vendor);
    setVendorActionType(action);
    setVendorDialogOpen(true);
  };

  // Get unique cost centers for filter dropdown
  const getUniqueCostCenters = () => {
    const costCenters = new Set();
    purchaseRequests.forEach(pr => {
      if (pr.cost_centre) {
        costCenters.add(pr.cost_centre);
      }
    });
    return Array.from(costCenters).sort();
  };

  const uniqueCostCenters = getUniqueCostCenters();

  // Purchase Request functions
  const getFilteredPRs = () => {
    let filtered = [];
    
    if (prTab === 'active') {
      filtered = purchaseRequests.filter(pr => pr.status === 'pending_finance_approval');
    } else {
      filtered = purchaseRequests.filter(pr => 
        pr.accounts_approved_by || 
        pr.status === 'tax_invoice_uploaded' ||
        (pr.status === 'additional_info_requested' && 
         pr.additional_info_conversation?.some(msg => msg.role === 'accounts'))
      );
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(pr => 
        pr.requested_by?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pr.cost_centre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pr.po_number?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply cost center filter
    if (costCenterFilter !== 'all') {
      filtered = filtered.filter(pr => pr.cost_centre === costCenterFilter);
    }

    return filtered;
  };

  const filteredPRs = getFilteredPRs();
  const totalPages = Math.ceil(filteredPRs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPRs = filteredPRs.slice(startIndex, endIndex);

  // Reset to page 1 when filters change or tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, prTab, costCenterFilter]);

  const handlePRAction = async () => {
    if (prActionType === 'request_info' && !additionalInfoRequest.trim()) {
      toast.error('Additional information request is required');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/purchase-requests/${selectedPR.id}/accounts-action`,
        { 
          action: prActionType, 
          additional_info_request: prActionType === 'request_info' ? additionalInfoRequest : null 
        },
        { headers: getAuthHeader() }
      );
      toast.success(
        prActionType === 'approve' 
          ? 'Documents verified and approved!' 
          : 'Additional information requested'
      );
      setPrDialogOpen(false);
      setAdditionalInfoRequest('');
      fetchPurchaseRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  const openPRActionDialog = (pr, action) => {
    setSelectedPR(pr);
    setPrActionType(action);
    setPrDialogOpen(true);
  };

  const openViewDialog = (pr) => {
    setSelectedPR(pr);
    setViewDialogOpen(true);
  };

  const getStatusBadge = (pr) => {
    // For active tab: show current status
    if (prTab === 'active') {
      if (pr.status === 'pending_finance_approval') return <Badge className="bg-blue-100 text-blue-800">Pending Finance Approval</Badge>;
    }
    
    // For history tab: show what Accounts did (their action, not final status)
    if (prTab === 'history') {
      if (pr.accounts_approved_by) {
        return <Badge className="bg-green-100 text-green-800">Approved</Badge>;
      }
      if (pr.additional_info_conversation?.some(msg => msg.role === 'accounts')) {
        return <Badge className="bg-orange-100 text-orange-800">Addl Info Requested</Badge>;
      }
    }
    
    // Fallback
    return <Badge className="bg-gray-100 text-gray-800">{pr.status}</Badge>;
  };

  const pendingVendorsCount = vendors.filter(v => v.approval_status === 'pending_approval').length;
  const activePRsCount = purchaseRequests.filter(pr => pr.status === 'pending_finance_approval').length;

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <div className="flex items-center space-x-3">
              <Building2 className="w-6 h-6 text-indigo-600" />
              <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>
                {selectedSection === 'vendors' ? 'Vendor Approval' : selectedSection === 'purchase_requests' ? 'Purchase Request Review' : 'Accounts Dashboard'}
              </h1>
            </div>
          </div>
        </div>
      </nav>

      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Vendor Section */}
        {selectedSection === 'vendors' && (
          <>
            <div className="mb-6 border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setVendorTab('pending')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    vendorTab === 'pending'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Pending Approval ({vendors.filter(v => v.approval_status === 'pending_approval').length})
                </button>
                <button
                  onClick={() => setVendorTab('history')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    vendorTab === 'history'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  History ({vendors.filter(v => v.approval_status !== 'pending_approval').length})
                </button>
              </nav>
            </div>

            {getFilteredVendors().length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {vendorTab === 'pending' ? 'No pending vendors' : 'No history'}
                  </h3>
                  <p className="text-gray-600">
                    {vendorTab === 'pending' 
                      ? 'All vendors have been reviewed' 
                      : 'Approved/rejected vendors will appear here'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {getFilteredVendors().map((vendor) => (
                  <Card key={vendor.id} className="card-hover border-0 shadow-lg">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg">{vendor.legal_entity_name}</CardTitle>
                          <CardDescription className="mt-1">
                            Trade Name: {vendor.trade_name} | Created by: {vendor.created_by}
                          </CardDescription>
                        </div>
                        <div className="flex flex-col items-end space-y-1">
                          {vendor.approval_status === 'pending_approval' && (
                            <Badge className="bg-yellow-100 text-yellow-800">Pending Approval</Badge>
                          )}
                          {vendor.approval_status === 'approved' && (
                            <Badge className="bg-green-100 text-green-800">Approved</Badge>
                          )}
                          {vendor.approval_status === 'rejected' && (
                            <Badge className="bg-red-100 text-red-800">Rejected</Badge>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="font-medium text-gray-700">GST Number:</span>
                            <span className="ml-2 text-gray-900">{vendor.gst_number}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">State:</span>
                            <span className="ml-2 text-gray-900">{vendor.state}</span>
                          </div>
                        </div>
                        
                        <div>
                          <span className="font-medium text-gray-700">Registered Address:</span>
                          <p className="text-sm text-gray-900 mt-1">{vendor.registered_address}</p>
                        </div>

                        <div className="grid grid-cols-3 gap-4 text-sm pt-3 border-t">
                          <div>
                            <span className="font-medium text-gray-700">POC Name:</span>
                            <p className="text-gray-900 mt-1">{vendor.poc_name}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">POC Mobile:</span>
                            <p className="text-gray-900 mt-1">{vendor.poc_mobile}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">POC Email:</span>
                            <p className="text-gray-900 mt-1">{vendor.poc_email}</p>
                          </div>
                        </div>

                        <div className="pt-3 border-t">
                          <span className="font-medium text-gray-700 block mb-2">Documents:</span>
                          <div className="flex space-x-3">
                            {vendor.cin_document_url && (
                              <a href={vendor.cin_document_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                                View CIN
                              </a>
                            )}
                            {vendor.pan_document_url && (
                              <a href={vendor.pan_document_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                                View PAN
                              </a>
                            )}
                            {vendor.gst_certificate_url && (
                              <a href={vendor.gst_certificate_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                                View GST Certificate
                              </a>
                            )}
                            {vendor.cheque_document_url && (
                              <a href={vendor.cheque_document_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                                View Cheque
                              </a>
                            )}
                          </div>
                        </div>
                      </div>

                      {vendor.approval_status === 'rejected' && vendor.rejection_reason && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-3">
                          <p className="text-sm text-red-800">
                            <span className="font-medium">Rejection Reason:</span> {vendor.rejection_reason}
                          </p>
                        </div>
                      )}

                      {vendor.approval_status === 'approved' && vendor.approved_by && (
                        <div className="bg-green-50 border border-green-200 rounded-md p-3">
                          <p className="text-sm text-green-800">
                            Approved by: {vendor.approved_by} on {new Date(vendor.approved_at).toLocaleDateString()}
                          </p>
                        </div>
                      )}

                      {vendorTab === 'pending' && (
                        <div className="flex space-x-2">
                          <Button onClick={() => openVendorActionDialog(vendor, 'approve')} className="bg-green-600 hover:bg-green-700">
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Approve
                          </Button>
                          <Button onClick={() => openVendorActionDialog(vendor, 'reject')} variant="destructive">
                            <X className="w-4 h-4 mr-2" />
                            Reject
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* Purchase Requests Section */}
        <>
            <div className="mb-6 border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setPrTab('active')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    prTab === 'active'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Active ({purchaseRequests.filter(pr => pr.status === 'pending_finance_approval').length})
                </button>
                <button
                  onClick={() => setPrTab('history')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    prTab === 'history'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  History ({purchaseRequests.filter(pr => 
                    pr.accounts_approved_by || 
                    pr.status === 'tax_invoice_uploaded' ||
                    (pr.status === 'additional_info_requested' && 
                     pr.additional_info_conversation?.some(msg => msg.role === 'accounts'))
                  ).length})
                </button>
              </nav>
            </div>

            <Card data-testid="accounts-request-list">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-900">
                  Account Review - Purchase Requests
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Filter and Search Controls */}
                  <div className="mb-4 flex flex-wrap gap-4 items-center justify-between">
                    <div className="flex gap-3 items-center flex-1">
                      <input
                        type="text"
                        placeholder="Search by requester, cost center, PO number..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 flex-1 max-w-md"
                      />
                      <select
                        value={costCenterFilter}
                        onChange={(e) => setCostCenterFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="all">All Cost Centers</option>
                        {uniqueCostCenters.map(cc => (
                          <option key={cc} value={cc}>{cc}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Show:</span>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </select>
                      <span className="text-sm text-gray-600">per page</span>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Requested By</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Cost Center</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Amount</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">PO Number</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Status</th>
                          <th className="px-3 py-3 text-center text-sm font-semibold text-gray-900">View</th>
                          {prTab === 'active' && (
                            <>
                              <th className="px-3 py-3 text-center text-sm font-semibold text-gray-900">Approve</th>
                              <th className="px-3 py-3 text-center text-sm font-semibold text-gray-900">Request Info</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {currentPRs.length === 0 ? (
                          <tr>
                            <td colSpan={prTab === 'active' ? "8" : "6"} className="px-4 py-8 text-center text-gray-500">
                              No requests found
                            </td>
                          </tr>
                        ) : (
                          currentPRs.map((pr) => (
                          <tr key={pr.id}>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {pr.requested_by}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {pr.cost_centre}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              ₹{(pr.base_amount || 0).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {pr.po_number || 'N/A'}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-col gap-1">
                                {getStatusBadge(pr)}
                                {pr.hold_status && (
                                  <Badge className="bg-red-100 text-red-800">On Hold</Badge>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-3 text-center">
                              <Button
                                onClick={() => openViewDialog(pr)}
                                variant="ghost"
                                size="sm"
                                className="text-indigo-600 hover:text-indigo-900"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </td>
                            {prTab === 'active' && (
                              <>
                                <td className="px-3 py-3 text-center">
                                  <Button
                                    onClick={() => openPRActionDialog(pr, 'approve')}
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700 text-white"
                                    disabled={pr.hold_status}
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </Button>
                                </td>
                                <td className="px-3 py-3 text-center">
                                  <Button
                                    onClick={() => openPRActionDialog(pr, 'request_info')}
                                    size="sm"
                                    variant="outline"
                                    className="border-orange-500 text-orange-600 hover:bg-orange-50"
                                    disabled={pr.hold_status}
                                  >
                                    <AlertCircle className="w-4 h-4" />
                                  </Button>
                                </td>
                              </>
                            )}
                          </tr>
                        ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Controls */}
                  {filteredPRs.length > 0 && (
                    <div className="mt-4 flex items-center justify-between">
                      <p className="text-sm text-gray-600">
                        Showing {startIndex + 1} to {Math.min(endIndex, filteredPRs.length)} of {filteredPRs.length} results
                      </p>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          variant="outline"
                          size="sm"
                        >
                          Previous
                        </Button>
                        <div className="flex gap-1">
                          {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter(page => {
                              return page === 1 || 
                                     page === totalPages || 
                                     (page >= currentPage - 1 && page <= currentPage + 1);
                            })
                            .map((page, index, array) => (
                              <div key={page} className="flex items-center">
                                {index > 0 && array[index - 1] !== page - 1 && (
                                  <span className="px-2 text-gray-400">...</span>
                                )}
                                <Button
                                  onClick={() => setCurrentPage(page)}
                                  variant={currentPage === page ? "default" : "outline"}
                                  size="sm"
                                  className={currentPage === page ? "bg-indigo-600 hover:bg-indigo-700" : ""}
                                >
                                  {page}
                                </Button>
                              </div>
                            ))
                          }
                        </div>
                        <Button
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                          variant="outline"
                          size="sm"
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
          </>

        {/* Vendor Action Dialog */}
        <Dialog open={vendorDialogOpen} onOpenChange={setVendorDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{vendorActionType === 'approve' ? 'Approve Vendor' : 'Reject Vendor'}</DialogTitle>
              <DialogDescription>
                {vendorActionType === 'approve'
                  ? 'This vendor will be approved and available for purchase requests.'
                  : 'Please provide a reason for rejecting this vendor.'}
              </DialogDescription>
            </DialogHeader>
            {vendorActionType === 'reject' && (
              <div className="space-y-2 mt-4">
                <Label htmlFor="vendor_rejection_reason">Rejection Reason *</Label>
                <textarea
                  id="vendor_rejection_reason"
                  value={vendorRejectionReason}
                  onChange={(e) => setVendorRejectionReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  rows={4}
                  placeholder="Enter reason for rejecting this vendor..."
                />
              </div>
            )}
            <div className="flex justify-end space-x-2 mt-6">
              <Button variant="outline" onClick={() => setVendorDialogOpen(false)} disabled={loading}>
                Cancel
              </Button>
              <Button
                onClick={handleVendorAction}
                className={vendorActionType === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
                variant={vendorActionType === 'reject' ? 'destructive' : 'default'}
                disabled={loading}
              >
                {loading ? 'Processing...' : (vendorActionType === 'approve' ? 'Approve' : 'Reject')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* PR Action Dialog */}
        <Dialog open={prDialogOpen} onOpenChange={setPrDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{prActionType === 'approve' ? 'Approve Documents' : 'Request Additional Information'}</DialogTitle>
              <DialogDescription>
                {prActionType === 'approve'
                  ? 'This will approve the documents and send to finance for final approval.'
                  : 'Request additional information or clarification from the requester.'}
              </DialogDescription>
            </DialogHeader>
            {prActionType === 'request_info' && (
              <div className="space-y-2 mt-4">
                <Label htmlFor="additional_info_request">Information Request *</Label>
                <textarea
                  id="additional_info_request"
                  value={additionalInfoRequest}
                  onChange={(e) => setAdditionalInfoRequest(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  rows={4}
                  placeholder="Enter your request for additional information..."
                />
              </div>
            )}
            <div className="flex justify-end space-x-2 mt-6">
              <Button variant="outline" onClick={() => setPrDialogOpen(false)} disabled={loading}>
                Cancel
              </Button>
              <Button
                onClick={handlePRAction}
                className={prActionType === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'border-orange-500 text-orange-600'}
                variant={prActionType === 'approve' ? 'default' : 'outline'}
                disabled={loading}
              >
                {loading ? 'Processing...' : (prActionType === 'approve' ? 'Approve' : 'Request Info')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* PO Dialog */}
        <Dialog open={poDialogOpen} onOpenChange={setPoDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            {poContent && (
              <>
                <DialogHeader>
                  <DialogTitle>Purchase Order: {poContent.po_number}</DialogTitle>
                  <DialogDescription>
                    Generated Purchase Order Document
                  </DialogDescription>
                </DialogHeader>
                <div className="mt-4">
                  <pre className="whitespace-pre-wrap font-mono text-xs bg-gray-50 p-4 rounded border border-gray-200">
                    {poContent.content}
                  </pre>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* View Details Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Purchase Request Details</DialogTitle>
              <DialogDescription>
                Complete view of the purchase request including invoice details and documents
              </DialogDescription>
            </DialogHeader>
            {selectedPR && (
              <div className="space-y-6">
                {/* Status Badges */}
                <div className="flex items-center gap-2">
                  {getStatusBadge(selectedPR)}
                  {selectedPR.hold_status && (
                    <Badge className="bg-red-100 text-red-800">On Hold</Badge>
                  )}
                </div>

                {/* Hold Reason if present */}
                {selectedPR.hold_status && selectedPR.hold_reason && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-red-800 mb-2">Hold Reason:</p>
                    <p className="text-sm text-red-700">{selectedPR.hold_reason}</p>
                    {selectedPR.held_by && selectedPR.held_at && (
                      <p className="text-xs text-red-600 mt-2">
                        Held by: {selectedPR.held_by} on {new Date(selectedPR.held_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                )}

                {/* Basic Information */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Requested By</p>
                    <p className="text-sm font-medium mt-1">{selectedPR.requested_by}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Cost Centre</p>
                    <p className="text-sm font-medium mt-1">{selectedPR.cost_centre}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Base Amount</p>
                    <p className="text-sm font-medium mt-1">₹{(selectedPR.base_amount || 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Inclusive Tax</p>
                    <p className="text-sm font-medium mt-1">{selectedPR.inclusive_tax || 'No'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">PO Number</p>
                    <p className="text-sm font-medium mt-1">{selectedPR.po_number || 'Not Generated'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Due Date</p>
                    <p className="text-sm font-medium mt-1">
                      {selectedPR.due_date ? new Date(selectedPR.due_date).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>

                {/* Reason */}
                <div>
                  <p className="text-sm text-gray-600 mb-2">Reason</p>
                  <p className="text-sm text-gray-900 p-3 bg-gray-50 rounded-lg">{selectedPR.reason || 'No reason provided'}</p>
                </div>

                {/* Vendor Name */}
                {selectedPR.vendor_id && (() => {
                  const vendor = vendors.find(v => v.id === selectedPR.vendor_id);
                  if (vendor) {
                    return (
                      <div>
                        <p className="text-sm text-gray-600 mb-2">Vendor</p>
                        <p className="text-sm font-medium text-gray-900 p-3 bg-gray-50 rounded-lg">
                          {vendor.legal_entity_name}
                        </p>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Tax Invoice Details Section */}
                {(selectedPR.tax_invoice_date || selectedPR.credit_period || selectedPR.tax_invoice_url) && (
                  <div className="border-t pt-4">
                    <h3 className="text-base font-semibold text-gray-900 mb-3">Tax Invoice Details</h3>
                    <div className="bg-green-50 rounded-lg p-4 space-y-3">
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        {selectedPR.tax_invoice_date && (
                          <div>
                            <span className="font-medium text-gray-700">Invoice Date:</span>
                            <p className="text-gray-900 mt-1">
                              {new Date(selectedPR.tax_invoice_date).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                        {selectedPR.credit_period && (
                          <div>
                            <span className="font-medium text-gray-700">Credit Period:</span>
                            <p className="text-gray-900 mt-1">{selectedPR.credit_period} days</p>
                          </div>
                        )}
                        {selectedPR.due_date && (
                          <div>
                            <span className="font-medium text-gray-700">Due Date:</span>
                            <p className="text-gray-900 mt-1">
                              {new Date(selectedPR.due_date).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                      </div>
                      {selectedPR.tax_invoice_url && (
                        <div className="pt-2 border-t border-green-200">
                          <Button
                            onClick={async () => await downloadFile(selectedPR.tax_invoice_url, `Tax_Invoice_${selectedPR.id}.pdf`)}
                            variant="outline"
                            size="sm"
                            className="text-indigo-600 hover:text-indigo-700"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download Tax Invoice
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Documents Section */}
                <div className="border-t pt-4">
                  <h3 className="text-base font-semibold text-gray-900 mb-3">Documents</h3>
                  
                  {(selectedPR.proforma_invoice_url || selectedPR.po_url) ? (
                    <div className="space-y-3">
                      {/* Proforma Invoice */}
                      {selectedPR.proforma_invoice_url && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="text-sm font-medium text-gray-900">Proforma Invoice</p>
                            <p className="text-xs text-gray-600">Purchase order document</p>
                          </div>
                          <Button
                            onClick={async () => await downloadFile(selectedPR.proforma_invoice_url, `Proforma_Invoice_${selectedPR.id}.pdf`)}
                            variant="outline"
                            size="sm"
                            className="text-indigo-600 hover:text-indigo-700"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </Button>
                        </div>
                      )}

                      {/* Purchase Order */}
                      {selectedPR.po_url && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="text-sm font-medium text-gray-900">Purchase Order</p>
                            <p className="text-xs text-gray-600">Generated PO: {selectedPR.po_number || 'N/A'}</p>
                          </div>
                          <Button
                            onClick={async () => await downloadFile(selectedPR.po_url, `PO_${selectedPR.po_number || selectedPR.id}.pdf`)}
                            variant="outline"
                            size="sm"
                            className="text-indigo-600 hover:text-indigo-700"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No documents uploaded</p>
                  )}
                </div>

                {/* Additional Info Conversation History */}
                {selectedPR.additional_info_conversation && selectedPR.additional_info_conversation.length > 0 && (
                  <div className="border-t pt-4">
                    <h3 className="text-base font-semibold text-gray-900 mb-3">Additional Information History</h3>
                    <div className="bg-orange-50 rounded-lg p-4 space-y-3">
                      <div className="space-y-2">
                        {selectedPR.additional_info_conversation.map((entry, index) => (
                          <div key={index} className={`p-3 rounded ${entry.type === 'request' ? 'bg-orange-100' : 'bg-blue-100'} text-sm`}>
                            <div className="flex justify-between items-start mb-2">
                              <span className="font-medium">
                                {entry.type === 'request' ? (entry.role === 'accounts' ? 'Accounts' : 'Finance') : 'Requester'}: {entry.user}
                              </span>
                              <span className="text-xs text-gray-500">
                                {new Date(entry.timestamp).toLocaleDateString('en-US', { 
                                  month: 'short', day: 'numeric', 
                                  hour: '2-digit', minute: '2-digit' 
                                })}
                              </span>
                            </div>
                            <p className="text-gray-900">{entry.message}</p>
                            {entry.docs && entry.docs.length > 0 && (
                              <div className="mt-1">
                                <span className="text-xs text-gray-600">Attachments: </span>
                                {entry.docs.map((doc, docIndex) => (
                                  <a 
                                    key={docIndex}
                                    href={doc} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-600 hover:underline ml-1"
                                  >
                                    Doc {docIndex + 1}
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Approval Information */}
                {selectedPR.approved_by && (
                  <div className="border-t pt-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm text-green-800">
                        <span className="font-medium">Approved by:</span> {selectedPR.approved_by} on {new Date(selectedPR.approved_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                )}

                {/* Metadata */}
                <div className="border-t pt-4 text-xs text-gray-500 space-y-1">
                  {selectedPR.created_at && (
                    <p>Created: {new Date(selectedPR.created_at).toLocaleString()}</p>
                  )}
                  {selectedPR.approved_at && (
                    <p>Approved: {new Date(selectedPR.approved_at).toLocaleString()}</p>
                  )}
                  {selectedPR.accounts_approved_at && (
                    <p>Accounts Approved: {new Date(selectedPR.accounts_approved_at).toLocaleString()}</p>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AccountsDashboard;